import React, { useEffect, useMemo, useState } from 'react';
import { Select, message } from 'antd';
import { TWButton } from '../../ui';
import SlideOver from '../../slide-over/slide-over';
import menuApi from '../../../services/menuApi';
import storeApi from '../../../services/storeApi';
import { useSelector } from 'react-redux';

const MenuPublishModal = ({ show, menu, menuList, onClose, onPublished }) => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [storeList, setStoreList] = useState([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [replaceIds, setReplaceIds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !globalCompany?.id) return;
    (async () => {
      try {
        const res = await storeApi.list(`companyid=${globalCompany.id}`);
        setStoreList(res.data.data || []);
      } catch (e) {
        setStoreList([]);
      }
    })();
    setSelectedStoreIds([]);
    setReplaceIds([]);
  }, [show, globalCompany?.id]);

  // Menus currently live on any selected store (candidates to replace)
  const conflictingMenus = useMemo(() => {
    if (!menu) return [];
    return (menuList || []).filter(
      (m) =>
        m.id !== menu.id &&
        (m.menustores || []).some((ms) =>
          selectedStoreIds.includes(ms.storeid)
        )
    );
  }, [menuList, menu, selectedStoreIds]);

  useEffect(() => {
    // Multiple live menus per store is the norm — replacing an existing menu
    // is opt-in, so conflicts start unchecked.
    setReplaceIds([]);
  }, [conflictingMenus]);

  const onSubmit = async () => {
    if (selectedStoreIds.length === 0) {
      message.warning('Select at least one store');
      return;
    }
    try {
      setLoading(true);
      await menuApi.publish({
        menuid: menu.id,
        storeids: selectedStoreIds,
        replacemenuids: replaceIds,
      });
      message.success('Menu is now live');
      onPublished();
      onClose();
    } catch (err) {
      message.error(err?.response?.data || 'Publish failed');
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="grid grid-cols-2 gap-3">
      <TWButton variant="default" label="Cancel" onClick={onClose} disabled={loading} />
      <TWButton
        variant="primary"
        label="Go Live"
        onClick={onSubmit}
        loading={loading}
        disabled={loading}
      />
    </div>
  );

  return (
    <SlideOver
      show={show}
      onClose={onClose}
      title={`Go Live — ${menu?.name || ''}`}
      subtitle="Choose the stores where this menu should be visible to customers."
      footer={footer}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400">
            Stores
          </label>
          <div className="mt-1">
            <Select
              mode="multiple"
              allowClear
              size="large"
              style={{ width: '100%' }}
              placeholder="Select stores"
              value={selectedStoreIds}
              onChange={setSelectedStoreIds}
              options={storeList.map((x) => ({ label: x.name, value: x.id }))}
            />
          </div>
        </div>
        {conflictingMenus.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/50 dark:bg-amber-900/20">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              These menus are already live on the selected stores:
            </p>
            <div className="mt-2 space-y-1">
              {conflictingMenus.map((m) => (
                <label
                  key={m.id}
                  className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200"
                >
                  <input
                    type="checkbox"
                    checked={replaceIds.includes(m.id)}
                    onChange={(e) =>
                      setReplaceIds((prev) =>
                        e.target.checked
                          ? [...prev, m.id]
                          : prev.filter((x) => x !== m.id)
                      )
                    }
                  />
                  Unassign “{m.name}” from the selected stores
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              Unchecked menus stay live alongside this one.
            </p>
          </div>
        )}
      </div>
    </SlideOver>
  );
};

export default MenuPublishModal;
